import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { Chip } from "@mui/material";

// project import
import MainCard from "components/MainCard";
import IconButton from "components/@extended/IconButton";
import Transitions from "components/@extended/Transitions";
import { ThemeMode } from "config";

// hooks & stores
import { GetPendingApprovalCounts } from "features/dashboard/api/hooks";
import useMemberStore from "features/member/store/memberStore";
import useEventStore from "features/event_New/store/eventStore";
import useAchievementStore from "features/achievement/store/AchievementStore";
import useJobPostingStore from "features/jobPosting/store/JobPostingStore";

// assets
import BellOutlined from "@ant-design/icons/BellOutlined";
import TeamOutlined from "@ant-design/icons/TeamOutlined";
import CalendarOutlined from "@ant-design/icons/CalendarOutlined";
import TrophyOutlined from "@ant-design/icons/TrophyOutlined";
import SolutionOutlined from "@ant-design/icons/SolutionOutlined";

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: "1rem",
};

const actionSX = {
  mt: "6px",
  ml: 1,
  top: "auto",
  right: "auto",
  alignSelf: "flex-start",
  transform: "none",
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const handleToggle = () => {
    setOpen((prevOpen: boolean) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const { data: pendingData } = GetPendingApprovalCounts();
  const counts = pendingData?.apiData;

  const pendingMembersCount = counts?.pendingMembersCount ?? 0;
  const pendingEventsCount = counts?.pendingEventsCount ?? 0;
  const pendingAchievementsCount = counts?.pendingAchievementsCount ?? 0;
  const pendingJobsCount = counts?.pendingJobsCount ?? 0;

  const totalPending =
    pendingMembersCount +
    pendingEventsCount +
    pendingAchievementsCount +
    pendingJobsCount;

  const handleNavigate = (
    path: string,
    type: "member" | "event" | "achievement" | "job",
  ) => {
    let state = {};
    if (type === "member") {
      useMemberStore.getState().handleFiltering({
        ...useMemberStore.getState().postModel.filterModel,
        memberStatus: 1, // Pending
      } as any);
      state = { memberStatus: 1 };
    } else if (type === "event") {
      useEventStore.getState().handleFiltering({
        ...useEventStore.getState().postModel.filterModel,
        eventStatus: 1, // Pending
      } as any);
      state = { eventStatus: 1 };
    } else if (type === "achievement") {
      useAchievementStore.getState().handleFiltering({
        ...useAchievementStore.getState().postModel.filterModel,
        verificationStatus: 1, // Pending
      } as any);
      state = { verificationStatus: 1 };
    } else if (type === "job") {
      useJobPostingStore.getState().handleFiltering({
        ...useJobPostingStore.getState().postModel.filterModel,
        verificationStatus: 1, // Pending
      } as any);
      state = { verificationStatus: 1 };
    }
    navigate(path, { state });
    setOpen(false);
  };

  const iconBackColorOpen =
    theme.palette.mode === ThemeMode.DARK ? "background.default" : "grey.100";

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{
          color: "text.primary",
          bgcolor: open ? iconBackColorOpen : "transparent",
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={totalPending} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            { name: "offset", options: { offset: [matchesXs ? -5 : 0, 9] } },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={matchesXs ? "top" : "top-right"}
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: "100%",
                minWidth: 285,
                maxWidth: { xs: 285, md: 420 },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Pending Approvals"
                  elevation={0}
                  border={false}
                  content={false}
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      "& .MuiListItemButton-root": {
                        py: 0.5,
                        "&.Mui-selected": {
                          bgcolor: "grey.50",
                          color: "text.primary",
                        },
                        "& .MuiAvatar-root": avatarSX,
                        "& .MuiListItemSecondaryAction-root": {
                          ...actionSX,
                          position: "relative",
                        },
                      },
                    }}
                  >
                    {/* Member Approvals */}
                    <ListItemButton
                      onClick={() => handleNavigate("/member", "member")}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            color: "primary.main",
                            bgcolor: "primary.lighter",
                          }}
                        >
                          <TeamOutlined />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">Member Approvals</Typography>
                        }
                        secondary={`${pendingMembersCount} members pending approval`}
                      />
                      <ListItemSecondaryAction>
                        {pendingMembersCount > 0 && (
                          <Chip
                            label="Pending"
                            size="small"
                            color="warning"
                            variant="light"
                            sx={{
                              borderRadius: 10,
                              fontSize: "0.625rem",
                              height: 16,
                            }}
                          />
                        )}
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    <Divider />

                    {/* Event Approvals */}
                    <ListItemButton
                      onClick={() => handleNavigate("/events", "event")}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            color: "success.main",
                            bgcolor: "success.lighter",
                          }}
                        >
                          <CalendarOutlined />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">Event Approvals</Typography>
                        }
                        secondary={`${pendingEventsCount} events pending approval`}
                      />
                      <ListItemSecondaryAction>
                        {pendingEventsCount > 0 && (
                          <Chip
                            label="Pending"
                            size="small"
                            color="warning"
                            variant="light"
                            sx={{
                              borderRadius: 10,
                              fontSize: "0.625rem",
                              height: 16,
                            }}
                          />
                        )}
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    <Divider />

                    {/* Achievement Approvals */}
                    <ListItemButton
                      onClick={() =>
                        handleNavigate("/achievements", "achievement")
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            color: "warning.main",
                            bgcolor: "warning.lighter",
                          }}
                        >
                          <TrophyOutlined />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                            Achievement Approvals
                          </Typography>
                        }
                        secondary={`${pendingAchievementsCount} achievements pending approval`}
                      />
                      <ListItemSecondaryAction>
                        {pendingAchievementsCount > 0 && (
                          <Chip
                            label="Pending"
                            size="small"
                            color="warning"
                            variant="light"
                            sx={{
                              borderRadius: 10,
                              fontSize: "0.625rem",
                              height: 16,
                            }}
                          />
                        )}
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    <Divider />

                    {/* Job Approvals */}
                    <ListItemButton
                      onClick={() => handleNavigate("/jobPosting", "job")}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{ color: "info.main", bgcolor: "info.lighter" }}
                        >
                          <SolutionOutlined />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">Job Approvals</Typography>
                        }
                        secondary={`${pendingJobsCount} jobs pending approval`}
                      />
                      <ListItemSecondaryAction>
                        {pendingJobsCount > 0 && (
                          <Chip
                            label="Pending"
                            size="small"
                            color="warning"
                            variant="light"
                            sx={{
                              borderRadius: 10,
                              fontSize: "0.625rem",
                              height: 16,
                            }}
                          />
                        )}
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
